import api from './api';

/**
 * AI Service
 *
 * Connects to backend AI endpoints for caption generation, content improvement,
 * and hashtag generation using OpenAI GPT-4.
 */

// ============================================================================
// Types
// ============================================================================

export interface GenerateCaptionRequest {
  topic?: string;
  tone?: 'professional' | 'casual' | 'funny' | 'formal' | 'friendly';
  platform?: string;
  length?: 'short' | 'medium' | 'long';
  channelIds?: number[];
  brandProfileId?: number;
}

export interface ImproveContentRequest {
  content: string;
  instruction: 'grammar' | 'emojis' | 'concise' | 'detailed' | 'hashtags' | string;
  platform?: string;
  brandProfileId?: number;
}

export interface AIResponse {
  content: string;
  hashtags?: string[];
  usage?: {
    tokens: number;
    cost: number;
  };
}

export interface AIUsage {
  totalTokens: number;
  totalCost: number;
  byJob?: Array<{
    jobType: string;
    count: number;
    tokens: number;
  }>;
}

export interface TranslateResponse {
  content: string;
  sourceLanguage: string;
  targetLanguage: string;
  usage?: {
    tokens: number;
    cost: number;
  };
}

// ============================================================================
// Service Functions
// ============================================================================

// AI calls need longer timeout — OpenAI can take 30-60s for long content
const AI_TIMEOUT = 90000;

export const aiService = {
  /**
   * Generate a new caption using AI
   *
   * POST /api/ai/caption
   */
  generateCaption: async (data: GenerateCaptionRequest): Promise<AIResponse> => {
    const response = await api.post('/api/ai/caption', {
      topic: data.topic,
      tone: data.tone || 'professional',
      platform: data.platform,
      length: data.length || 'medium',
      channelIds: data.channelIds,
      brandProfileId: data.brandProfileId,
    }, { timeout: AI_TIMEOUT });
    // Backend returns { caption }, normalize to { content } for consistency
    const result = response.data.data;
    return {
      content: result.caption || result.content,
      hashtags: result.hashtags,
      usage: result.usage,
    };
  },

  /**
   * Improve existing content
   *
   * POST /api/ai/improve
   */
  improveContent: async (data: ImproveContentRequest): Promise<AIResponse> => {
    const instructions = {
      grammar: 'Fix grammar and spelling errors while preserving the meaning',
      emojis: 'Add relevant emojis to make the content more engaging',
      concise: 'Make the content more concise while keeping the key message',
      detailed: 'Expand the content with more details and context',
      hashtags: 'Add relevant hashtags at the end',
    };

    const response = await api.post('/api/ai/improve', {
      content: data.content,
      instruction: instructions[data.instruction as keyof typeof instructions] || data.instruction,
      platform: data.platform,
      brandProfileId: data.brandProfileId,
    }, { timeout: AI_TIMEOUT });
    // Backend returns { improved }, normalize to { content } for consistency
    const result = response.data.data;
    return {
      content: result.improved || result.content,
      usage: result.usage,
    };
  },

  /**
   * Generate hashtags for content
   *
   * POST /api/ai/hashtags
   */
  generateHashtags: async (
    content: string,
    platform?: string,
    count?: number,
    brandProfileId?: number
  ): Promise<string[]> => {
    const response = await api.post('/api/ai/hashtags', {
      content,
      platform,
      count: count || 10,
      brandProfileId,
    }, { timeout: AI_TIMEOUT });
    return response.data.data.hashtags;
  },

  /**
   * Get user's AI usage stats
   *
   * GET /api/ai/usage
   */
  getUsage: async (): Promise<AIUsage> => {
    const response = await api.get('/api/ai/usage');
    return response.data.data;
  },

  /**
   * Translate caption to target language
   *
   * POST /api/ai/translate
   */
  translateCaption: async (
    content: string,
    targetLanguage: string,
    sourceLanguage?: string
  ): Promise<TranslateResponse> => {
    const response = await api.post('/api/ai/translate', {
      content,
      targetLanguage,
      sourceLanguage,
    }, { timeout: AI_TIMEOUT });
    return response.data.data;
  },
};

export default aiService;
